import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sparkle, Lightning, ChartBar, FileText, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOOLS = [
  { id: 'categorize', name: 'Smart Categorization', desc: 'Auto-suggest categories for transactions', icon: ChartBar, color: 'from-blue-500 to-cyan-500' },
  { id: 'draft', name: 'Document Drafting', desc: 'Draft quotes, agreements, reminders', icon: FileText, color: 'from-indigo-500 to-blue-500' },
  { id: 'explain', name: 'Anomaly Explainer', desc: 'Plain-English explanations of flagged items', icon: Lightning, color: 'from-cyan-500 to-teal-500' },
];

export function AIAssist() {
  const [activeTool, setActiveTool] = useState('categorize');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Categorize state
  const [catDesc, setCatDesc] = useState('');
  const [catAmount, setCatAmount] = useState('');
  const [catType, setCatType] = useState('expense');

  // Draft state
  const [draftType, setDraftType] = useState('quote');
  const [draftContext, setDraftContext] = useState({});
  const [draftContextRaw, setDraftContextRaw] = useState('Client Name: \nDescription: \nAmount: \nDate: \nDue Date: ');

  // Explain state
  const [anomType, setAnomType] = useState('duplicate_payment');
  const [anomDesc, setAnomDesc] = useState('');

  const handleCategorize = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/ai/categorize-expense`,
        { description: catDesc, amount: parseFloat(catAmount) || 0, type: catType },
        { withCredentials: true }
      );
      setResult({ type: 'category', value: data.category });
    } catch (e) {
      toast.error('AI service failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDraft = async () => {
    setLoading(true);
    setResult(null);
    try {
      // Parse the context from raw text (Key: Value lines)
      const context = {};
      draftContextRaw.split('\n').forEach(line => {
        const [k, ...rest] = line.split(':');
        if (k && rest.length) {
          context[k.trim().toLowerCase().replace(/\s+/g, '_')] = rest.join(':').trim();
        }
      });
      const { data } = await axios.post(`${API}/ai/draft-document`,
        { document_type: draftType, context },
        { withCredentials: true }
      );
      setResult({ type: 'draft', value: data.draft });
    } catch (e) {
      toast.error('AI service failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExplain = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/ai/explain-anomaly`,
        { anomaly_type: anomType, description: anomDesc },
        { withCredentials: true }
      );
      setResult({ type: 'explanation', value: data.explanation });
    } catch (e) {
      toast.error('AI service failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (result?.value) {
      navigator.clipboard.writeText(result.value);
      toast.success('Copied to clipboard');
    }
  };

  return (
    <div className="p-8" data-testid="ai-assist-page">
      <div className="mb-8 flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl ocean-gradient flex items-center justify-center text-white shadow-lg">
          <Sparkle size={24} weight="fill" />
        </div>
        <div>
          <h1 className="text-4xl font-bold tracking-tight ocean-text" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}} data-testid="ai-title">AI Assist</h1>
          <p className="mt-1 text-slate-600">Smart bookkeeping powered by Claude Sonnet 4.5</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {TOOLS.map(tool => {
          const Icon = tool.icon;
          const isActive = activeTool === tool.id;
          return (
            <button
              key={tool.id}
              onClick={() => { setActiveTool(tool.id); setResult(null); }}
              data-testid={`ai-tool-${tool.id}`}
              className={`glass-card p-5 text-left transition-all ${isActive ? 'ring-2 ring-blue-600 shadow-xl' : ''}`}
            >
              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tool.color} flex items-center justify-center text-white mb-3 shadow-md`}>
                <Icon size={20} weight="fill" />
              </div>
              <h3 className="font-bold text-slate-900" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>{tool.name}</h3>
              <p className="text-xs text-slate-600 mt-1">{tool.desc}</p>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-card p-6">
          <h2 className="text-xl font-bold mb-4" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Input</h2>
          
          {activeTool === 'categorize' && (
            <div className="space-y-4" data-testid="categorize-form">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Transaction Type</Label>
                <Select value={catType} onValueChange={setCatType}>
                  <SelectTrigger className="mt-2 bg-white/60" data-testid="categorize-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">Income</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Description</Label>
                <Input value={catDesc} onChange={(e) => setCatDesc(e.target.value)} placeholder="e.g. Vodacom monthly subscription" data-testid="categorize-desc-input" className="mt-2 bg-white/60" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Amount (R)</Label>
                <Input type="number" step="0.01" value={catAmount} onChange={(e) => setCatAmount(e.target.value)} data-testid="categorize-amount-input" className="mt-2 bg-white/60" />
              </div>
              <Button onClick={handleCategorize} disabled={loading || !catDesc} className="w-full ocean-gradient text-white h-11 rounded-lg shadow-md" data-testid="categorize-submit-button">
                <Sparkle size={18} className="mr-2" /> {loading ? 'Thinking...' : 'Suggest Category'}
              </Button>
            </div>
          )}

          {activeTool === 'draft' && (
            <div className="space-y-4" data-testid="draft-form">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Document Type</Label>
                <Select value={draftType} onValueChange={setDraftType}>
                  <SelectTrigger className="mt-2 bg-white/60" data-testid="draft-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quote">Quote</SelectItem>
                    <SelectItem value="invoice">Invoice Cover Note</SelectItem>
                    <SelectItem value="agreement">Service Agreement</SelectItem>
                    <SelectItem value="reminder">Payment Reminder</SelectItem>
                    <SelectItem value="follow_up">Follow-up Email</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Context (Key: Value per line)</Label>
                <Textarea
                  value={draftContextRaw}
                  onChange={(e) => setDraftContextRaw(e.target.value)}
                  rows={8}
                  placeholder="Client Name: Acme Corp&#10;Description: Web design&#10;Amount: R 15000"
                  className="mt-2 bg-white/60 font-mono text-sm"
                  data-testid="draft-context-input"
                />
              </div>
              <Button onClick={handleDraft} disabled={loading} className="w-full ocean-gradient text-white h-11 rounded-lg shadow-md" data-testid="draft-submit-button">
                <Sparkle size={18} className="mr-2" /> {loading ? 'Drafting...' : 'Draft Document'}
              </Button>
            </div>
          )}

          {activeTool === 'explain' && (
            <div className="space-y-4" data-testid="explain-form">
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Anomaly Type</Label>
                <Select value={anomType} onValueChange={setAnomType}>
                  <SelectTrigger className="mt-2 bg-white/60" data-testid="explain-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="duplicate_payment">Duplicate Payment</SelectItem>
                    <SelectItem value="missing_category">Missing Category</SelectItem>
                    <SelectItem value="unusual_value">Unusual Value</SelectItem>
                    <SelectItem value="missing_pop">Missing Proof of Payment</SelectItem>
                    <SelectItem value="vat_inconsistency">VAT Inconsistency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-widest text-slate-600">Details</Label>
                <Textarea
                  value={anomDesc}
                  onChange={(e) => setAnomDesc(e.target.value)}
                  rows={6}
                  placeholder="e.g. Payment of R12,500 on 15 Feb 2026 with no matching invoice or PO"
                  className="mt-2 bg-white/60"
                  data-testid="explain-desc-input"
                />
              </div>
              <Button onClick={handleExplain} disabled={loading || !anomDesc} className="w-full ocean-gradient text-white h-11 rounded-lg shadow-md" data-testid="explain-submit-button">
                <Sparkle size={18} className="mr-2" /> {loading ? 'Analyzing...' : 'Explain & Advise'}
              </Button>
            </div>
          )}
        </div>

        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>Result</h2>
            {result && (
              <Button onClick={copyToClipboard} variant="outline" size="sm" className="bg-white/60" data-testid="copy-result-button">Copy</Button>
            )}
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-16" data-testid="ai-loading">
              <div className="flex items-center gap-3 text-slate-600">
                <ArrowsClockwise size={24} className="animate-spin" />
                <span>Thinking...</span>
              </div>
            </div>
          ) : result ? (
            <div className="space-y-3" data-testid="ai-result">
              {result.type === 'category' && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Suggested Category</p>
                  <div className="text-3xl font-bold ocean-text" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>{result.value}</div>
                </div>
              )}
              {result.type === 'draft' && (
                <div className="whitespace-pre-wrap text-sm text-slate-800 leading-relaxed bg-white/50 p-4 rounded-lg border border-slate-200">
                  {result.value}
                </div>
              )}
              {result.type === 'explanation' && (
                <div className="text-sm text-slate-800 leading-relaxed bg-white/50 p-4 rounded-lg border border-slate-200">
                  {result.value}
                </div>
              )}
            </div>
          ) : (
            <div className="py-16 text-center text-slate-400" data-testid="ai-empty">
              <Sparkle size={48} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Submit an input to see AI suggestions</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
